// 1:1 port from age-of-mcp v3 (monument.ts). The flattened part literals are
// reconstructed from the original helper functions; geometry is parity-gated
// identical. The renderer's donation tier (0..5) is not modelled here — the
// default tier 0 (a bare stone stele on the plinth) is rendered.
//
// v3 monument — ONE BOLD STATUE SILHOUETTE PER TIER. A two-step stone plinth
// fills the 3x3 plot and the summit carries a single readable effigy whose SIZE
// and MATERIAL colour announce the donation tier. FOOTPRINT-NATIVE 3x3; no door.

import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { GEM, ROOF_PRECIOUS, STEEL, STONE, STONE_DK, TIMBER, WOOD, box, cyl, poles, slab } = styleV3;

const STEEL_LIT = "#9fb3c2";   // polished steel reads lighter than raw STEEL
const TAU = Math.PI * 2;

// The two-step plinth on its foundation slab; its top is where effigies stand.
const PTOP = 0.84;

function plinth(): Part[] {
  return [
    slab(2.9, 2.9),
    box(2.1, 0.4, 2.1, STONE, [0, 0.12 + 0.2, 0]),
    box(1.4, 0.32, 1.4, STONE_DK, [0, 0.52 + 0.16, 0]),
  ];
}

// A standing figure sized by `k`: robe cone, torso, head.
function figure(k: number, color: string): Part[] {
  return [
    { geometry: new THREE.ConeGeometry(0.3 * k, 0.7 * k, 6), color,
      position: [0, PTOP + 0.35 * k, 0] },
    box(0.3 * k, 0.32 * k, 0.2 * k, color, [0, PTOP + 0.84 * k, 0]),
    { geometry: new THREE.SphereGeometry(0.12 * k, 8, 6), color,
      position: [0, PTOP + 1.1 * k, 0] },
  ];
}

// Tier 0 — the bare plinth: a stout uncarved stele awaiting donations.
function stele(): Part[] {
  return [
    cyl(0.26, 0.34, 0.56, 6, STONE_DK, [0, PTOP + 0.28, 0]),
    box(0.5, 0.12, 0.5, STONE, [0, PTOP + 0.56 + 0.06, 0]),
  ];
}

// Tier 1 — the carved wooden totem: tapering trunk, crossed arms, head block.
function totem(): Part[] {
  return [
    box(0.44, 0.1, 0.44, TIMBER, [0, PTOP + 0.05, 0]),
    cyl(0.13, 0.18, 1.0, 6, WOOD, [0, PTOP + 0.6, 0]),
    box(0.4, 0.07, 0.07, WOOD, [-0.2, PTOP + 0.92, 0], [0, 0, 0.5]),
    box(0.4, 0.07, 0.07, WOOD, [0.2, PTOP + 0.92, 0], [0, 0, -0.5]),
    box(0.2, 0.24, 0.16, TIMBER, [0, PTOP + 1.24, 0]),
  ];
}

// The summit effigy for the donation tier.
function effigy(tier: number): Part[] {
  if (tier <= 0) return stele();
  if (tier === 1) return totem();
  if (tier === 2) {
    // The robed stone guardian leaning on a staff.
    return [
      ...figure(1.3, STONE),
      cyl(0.025, 0.025, 1.0, 5, STONE_DK, [0.3, PTOP + 0.5, 0]),
    ];
  }
  if (tier === 3) {
    // The polished steel guardian raising a sword, dark finials at the corners.
    const parts: Part[] = [
      ...figure(1.5, STEEL_LIT),
      box(0.05, 0.7, 0.03, STEEL_LIT, [0.34, PTOP + 1.6, 0]),
      box(0.16, 0.05, 0.06, STEEL, [0.34, PTOP + 1.26, 0]),
    ];
    for (const sx of [-1, 1]) {
      parts.push({ geometry: new THREE.SphereGeometry(0.06, 6, 5), color: STEEL,
        position: [sx * 0.6, PTOP + 0.06, sx * 0.6] });
      parts.push({ geometry: new THREE.SphereGeometry(0.06, 6, 5), color: STEEL,
        position: [sx * 0.6, PTOP + 0.06, -sx * 0.6] });
    }
    return parts;
  }
  // Tiers 4 and 5 — the golden colossus lifting a sun disc, arms raised,
  // gold banding on the plinth.
  const parts: Part[] = [
    box(0.56, 0.12, 0.56, ROOF_PRECIOUS, [0, PTOP + 0.06, 0]),
    ...figure(1.7, ROOF_PRECIOUS),
    cyl(0.045, 0.045, 0.56, 5, ROOF_PRECIOUS, [-0.28, PTOP + 1.95, 0], [0, 0, 0.5]),
    cyl(0.045, 0.045, 0.56, 5, ROOF_PRECIOUS, [0.28, PTOP + 1.95, 0], [0, 0, -0.5]),
    // The sun disc held aloft.
    cyl(0.3, 0.3, 0.05, 8, ROOF_PRECIOUS, [0, PTOP + 2.42, 0], [Math.PI / 2, 0, 0]),
    // Gold banding along the lower plinth step.
    box(2.14, 0.05, 0.07, ROOF_PRECIOUS, [0, 0.52, 1.04]),
    box(2.14, 0.05, 0.07, ROOF_PRECIOUS, [0, 0.52, -1.04]),
    box(0.07, 0.05, 2.14, ROOF_PRECIOUS, [1.04, 0.52, 0]),
    box(0.07, 0.05, 2.14, ROOF_PRECIOUS, [-1.04, 0.52, 0]),
  ];
  if (tier >= 5) {
    // The jewelled wonder: a great crowning gem, gem-tipped corner obelisks
    // and a ring of inlaid gems on the plinth.
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.2, 0), color: GEM,
      position: [0, PTOP + 2.76, 0] });
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        parts.push(
          box(0.13, 0.5, 0.13, STONE, [sx * 0.85, 0.52 + 0.25, sz * 0.85]),
          { geometry: new THREE.IcosahedronGeometry(0.08, 0), color: GEM,
            position: [sx * 0.85, 0.52 + 0.58, sz * 0.85] },
        );
      }
    }
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU;
      parts.push({ geometry: new THREE.IcosahedronGeometry(0.06, 0), color: GEM,
        position: [Math.cos(a) * 0.56, PTOP + 0.04, Math.sin(a) * 0.56] });
    }
  }
  return parts;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(2.9, 2.9), box(2.1, 0.4, 2.1, STONE, [0, 0.12 + 0.2, 0])];
  if (s === 1) return [...plinth()];
  if (s === 2) return [...plinth(), ...stele(), ...poles(2.2, 2.2, 1.2)];
  // Finished — the plinth crowned by the current material tier's effigy
  // (default tier 0).
  return [...plinth(), ...effigy(0)];
}

export const monumentV3 = defineModel({
  id: "monument-v3",
  tiles: { w: 3, h: 3 },
  batchId: "45-optimized",
  name: "Monument (v3)",
  tags: ["building","monument","low-poly","age-of-mcp","v3"],
  summary: "Low-poly monument.",
  description: "A colossal landmark built as a plain plinth and enriched with refined goods — a grand prestige piece.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
